import {
  DeleteOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Badge,
  Button,
  Card,
  DatePicker,
  Empty,
  Modal,
  Popover,
  Select,
  Tag,
  Tooltip,
  message,
} from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import { uniqBy } from 'lodash';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BACKEND_DATE_FORMAT,
  DATE_TIME_FORMAT,
  LIMIT,
  MODULES_KEY,
  PERMISSION_OBJECT,
  RECORDINGS_TABLE_FIELDS,
  RECORDING_STATUS,
  ROUTES_MODULES_KEY,
} from '../../common/constants';
import CommonTable from '../../components/CommonTable';
import SearchComponent from '../../components/SearchComponent';
import VerifyPermissions from '../../components/VerifyPermissions';
import history from '../../historyData';
import { useTableQueryParams } from '../../hooks/useTableQueryParams';
import { DELETE_RECORDING } from './graphql/Mutations';
import {
  MEETSENSE_WORKSPACES,
  RECORDINGS,
  RECORDING_DETAIL,
} from './graphql/Queries';

const buildRecordingsFilter = (params) => ({
  workspaceId: params?.workspaceId || undefined,
  skip: (params?.page - 1) * params?.pageSize,
  limit: params?.pageSize,
  searchString: params?.search,
  projectId: params?.projectId || undefined,
  status: params?.status || undefined,
  startDate: params?.recordingDate?.from
    ? dayjs(params?.recordingDate?.from)?.startOf('day')?.toISOString()
    : undefined,
  endDate: params?.recordingDate?.to
    ? dayjs(params?.recordingDate?.to)?.endOf('day')?.toISOString()
    : undefined,
});

// workspaceId is functionally required (the detail query needs it), so it stays in the
// URL — workspaceName is display-only and travels via router state instead, keeping the
// link shorter without losing anything a shared/refreshed link would need to work
const recordingDetailPath = (record, workspaceId) => ({
  pathname: `${ROUTES_MODULES_KEY?.RECORDINGS}/${record?.id}`,
  search: `?${new URLSearchParams({
    workspaceId: workspaceId || '',
  })?.toString()}`,
});

function Recordings() {
  // no separate download permission — anyone who can see a recording can download it
  const isDeletePermission = VerifyPermissions({
    modulekey: MODULES_KEY?.RECORDINGS,
    allowedPermissions: [PERMISSION_OBJECT?.delete],
  });

  const { params, setParams } = useTableQueryParams({
    fields: RECORDINGS_TABLE_FIELDS,
  });

  const [executeQuery, { loading, data }] = useLazyQuery(RECORDINGS, {
    fetchPolicy: 'network-only',
    onError() {},
  });

  const [workspaces, setWorkspaces] = useState([]);
  const [workspaceSkip, setWorkspaceSkip] = useState(0);
  const [isWorkspaceDataEnd, setIsWorkspaceDataEnd] = useState(false);

  const [fetchWorkspaces, { loading: workspacesLoading }] = useLazyQuery(
    MEETSENSE_WORKSPACES,
    {
      fetchPolicy: 'network-only',
      onError() {},
      onCompleted: (res) => {
        const newWorkspaces = res?.meetsenseWorkspaces?.data ?? [];
        setIsWorkspaceDataEnd(newWorkspaces?.length < LIMIT);
        setWorkspaces((prevData) =>
          uniqBy([...prevData, ...newWorkspaces], 'id'),
        );

        // most orgs only ever have one workspace — default to it so the page works
        // out of the box without anyone having to think about workspaces at all
        if (params?.workspaceId) return;
        const defaultWorkspace =
          newWorkspaces?.find((workspace) => workspace?.isDefault) ||
          newWorkspaces?.[0];
        if (defaultWorkspace?.id) {
          // also default to its first project — a workspace with no project selected
          // can only be browsed if it has its own MeetSense token configured, so
          // picking one up front avoids that failure for workspaces that don't
          setParams({
            workspaceId: defaultWorkspace?.id,
            projectId: defaultWorkspace?.projects?.[0]?.id || '',
          });
        }
      },
    },
  );

  useEffect(() => {
    fetchWorkspaces({ variables: { filters: { skip: 0, limit: LIMIT } } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // the selected workspace may live beyond the first page (e.g. it was
  // reached by scrolling before navigating away) — a route change remounts
  // this page and resets the loaded list to page one, so without this the
  // Select can't resolve a label for the saved id and falls back to showing
  // the raw id. Keep paginating until the selected workspace is found.
  useEffect(() => {
    if (
      !params?.workspaceId ||
      workspaces?.length === 0 ||
      isWorkspaceDataEnd ||
      workspacesLoading
    )
      return;
    const isSelectedWorkspaceLoaded = workspaces?.some(
      (workspace) => workspace?.id === params?.workspaceId,
    );
    if (isSelectedWorkspaceLoaded) return;
    const newSkip = workspaceSkip + LIMIT;
    fetchWorkspaces({
      variables: { filters: { skip: newSkip, limit: LIMIT } },
    });
    setWorkspaceSkip(newSkip);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaces, params?.workspaceId, isWorkspaceDataEnd, workspacesLoading]);

  const handleWorkspacePopupScroll = async (e) => {
    const nearBottom =
      e?.target?.scrollTop + e?.target?.clientHeight ===
      e?.target?.scrollHeight;
    if (nearBottom && !isWorkspaceDataEnd) {
      const newSkip = workspaceSkip + LIMIT;
      await fetchWorkspaces({
        variables: { filters: { skip: newSkip, limit: LIMIT } },
      });
      setWorkspaceSkip(newSkip);
    }
  };

  const workspaceOptions = workspaces?.map((workspace) => ({
    value: workspace?.id,
    label: workspace?.createdBy?.name
      ? `${workspace?.name} (${workspace?.createdBy?.name})`
      : workspace?.name,
  }));
  const selectedWorkspace = workspaces?.find(
    (workspace) => workspace?.id === params?.workspaceId,
  );
  const selectedWorkspaceName = selectedWorkspace?.name;
  // MeetSense already nests a workspace's projects in the same meetsenseWorkspaces
  // call, so the project filter is a plain client-side filter — no second query
  const projectOptions =
    selectedWorkspace?.projects?.map((project) => ({
      value: project?.id,
      label: project?.name,
    })) || [];
  // a workspace with no projects at all can't be queried (unless it has its
  // own MeetSense token, which we can't detect from here) — skip the API
  // call rather than let it fail, and show an explanatory empty state instead
  const isWorkspaceProjectsEmpty =
    !!selectedWorkspace && (selectedWorkspace?.projects?.length ?? 0) === 0;

  const [downloadingId, setDownloadingId] = useState(null);

  const [fetchAudioUrl] = useLazyQuery(RECORDING_DETAIL, {
    fetchPolicy: 'network-only',
    onError() {
      setDownloadingId(null);
    },
    onCompleted: (res) => {
      const audioUrl = res?.recording?.audioUrl;
      const title = res?.recording?.recording?.title || 'recording';
      if (!audioUrl) {
        message?.warning('Audio is not available for this recording yet.');
        setDownloadingId(null);
        return;
      }
      axios
        ?.get(audioUrl, { responseType: 'blob' })
        ?.then((response) => {
          const extension =
            audioUrl?.split('?')?.[0]?.split('.')?.pop() || 'mp3';
          // eslint-disable-next-line no-undef
          const blobUrl = window.URL.createObjectURL(response?.data);
          // eslint-disable-next-line no-undef
          const a = document.createElement('a');
          a.href = blobUrl;
          a.download = `${title}.${extension}`;
          // eslint-disable-next-line no-undef
          document.body.appendChild(a);
          a.click();
          // eslint-disable-next-line no-undef
          document.body.removeChild(a);
          // eslint-disable-next-line no-undef
          window.URL.revokeObjectURL(blobUrl);
        })
        ?.catch(() => message?.error('Unable to download the recording.'))
        ?.finally(() => setDownloadingId(null));
    },
  });

  useEffect(() => {
    if (!params?.workspaceId || isWorkspaceProjectsEmpty) return;
    executeQuery({ variables: { filters: buildRecordingsFilter(params) } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, isWorkspaceProjectsEmpty]);

  const handleSearch = (value) => setParams({ search: value?.trim(), page: 1 });
  const handleWorkspaceFilterChange = (value) => {
    // default to the newly selected workspace's first project — same reasoning as the
    // initial auto-select: browsing without a project needs that workspace's own token
    const newWorkspace = workspaces?.find(
      (workspace) => workspace?.id === value,
    );
    setParams({
      workspaceId: value,
      projectId: newWorkspace?.projects?.[0]?.id || '',
      page: 1,
    });
  };
  // at least one project must stay selected — a workspace with no project
  // selected can only be queried successfully if it has its own MeetSense token
  const handleProjectFilterChange = (value) =>
    setParams({
      projectId: value || selectedWorkspace?.projects?.[0]?.id || '',
      page: 1,
    });
  const handleStatusFilterChange = (value) =>
    setParams({ status: value, page: 1 });
  const handleDateRangeChange = (dates) => {
    setParams({
      recordingDate: {
        from: dates?.[0] ? dates?.[0]?.format(BACKEND_DATE_FORMAT) : null,
        to: dates?.[1] ? dates?.[1]?.format(BACKEND_DATE_FORMAT) : null,
      },
      page: 1,
    });
  };

  const disabledDate = (current) =>
    current && current?.isAfter(dayjs().endOf('day'));

  const activeFilterCount = [
    !!params?.workspaceId,
    !!params?.projectId,
    !!params?.status,
    !!(params?.recordingDate?.from && params?.recordingDate?.to),
  ]?.filter(Boolean)?.length;

  const handleClearAllFilters = () =>
    setParams({
      status: null,
      recordingDate: { from: null, to: null },
      page: 1,
    });

  const onChange = (pagination) => {
    const pageSizeChanged = pagination?.pageSize !== params?.pageSize;
    setParams({
      page: pageSizeChanged ? 1 : pagination?.current,
      pageSize: pagination?.pageSize,
    });
  };

  const [deleteRecording] = useMutation(DELETE_RECORDING, {
    onCompleted: (res) => {
      message?.success(res?.deleteRecording?.message);
      executeQuery({ variables: { filters: buildRecordingsFilter(params) } });
    },
    onError() {},
  });

  const handleDownload = (record) => {
    setDownloadingId(record?.id);
    fetchAudioUrl({
      variables: { id: record?.id, workspaceId: params?.workspaceId },
    });
  };

  const handleDelete = (record) => {
    Modal?.confirm({
      icon: <ExclamationCircleOutlined />,
      title: 'Delete Recording',
      content: 'Are you sure you want to delete this recording?',
      centered: true,
      closable: false,
      okText: 'Yes',
      cancelText: 'No',
      okButtonProps: { danger: true },
      className: 'custom-modal-button',
      onOk: () =>
        deleteRecording({
          variables: { id: record?.id, workspaceId: params?.workspaceId },
        }),
    });
  };

  const handleRowClick = (record) => {
    history?.push({
      ...recordingDetailPath(record, params?.workspaceId),
      state: { workspaceName: selectedWorkspaceName },
    });
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: 300,
      ellipsis: true,
      render: (value, record) => (
        <Link
          to={{
            ...recordingDetailPath(record, params?.workspaceId),
            state: { workspaceName: selectedWorkspaceName },
          }}
        >
          {value}
        </Link>
      ),
    },
    {
      title: 'Project',
      dataIndex: ['project', 'name'],
      key: 'project',
      render: (value) => value || '-',
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value) => (value ? dayjs(value)?.format(DATE_TIME_FORMAT) : '-'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      render: (value) => (
        <Tag color={RECORDING_STATUS?.[value]?.color}>
          {RECORDING_STATUS?.[value]?.label || value}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      render: (value, record) => (
        <div onClick={(e) => e?.stopPropagation()}>
          <Tooltip title="Download">
            <Button
              type="text"
              icon={<DownloadOutlined />}
              loading={downloadingId === record?.id}
              onClick={() => handleDownload(record)}
            />
          </Tooltip>
          {isDeletePermission && (
            <Tooltip title="Delete">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record)}
              />
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="table-card-page recordings-page">
      <Card
        className="ant-body-scroll"
        title={
          <>
            Recordings
            <div className="recordings-header">
              <div className="recordings-filters">
                <SearchComponent
                  key={params?.search || ''}
                  defaultValue={params?.search}
                  getData={handleSearch}
                  name="Title"
                />
                <Popover
                  placement="bottomRight"
                  trigger="click"
                  overlayClassName="recordings-filter-popover"
                  title={
                    <div className="recordings-filter-popover-title">
                      <span>Filters</span>
                      <Button
                        type="link"
                        size="small"
                        disabled={activeFilterCount === 0}
                        onClick={handleClearAllFilters}
                      >
                        Clear All
                      </Button>
                    </div>
                  }
                  content={
                    <div className="recordings-filter-list">
                      <div className="recordings-filter-item">
                        <span className="recordings-filter-label">
                          Workspace
                        </span>
                        <Select
                          placeholder="Workspace"
                          showSearch
                          optionFilterProp="label"
                          value={params?.workspaceId || undefined}
                          onChange={handleWorkspaceFilterChange}
                          onPopupScroll={handleWorkspacePopupScroll}
                          options={workspaceOptions}
                          virtual={false}
                        />
                      </div>
                      <div className="recordings-filter-item">
                        <span className="recordings-filter-label">Project</span>
                        <Select
                          placeholder="Select Project"
                          showSearch
                          optionFilterProp="label"
                          value={params?.projectId || undefined}
                          onChange={handleProjectFilterChange}
                          options={projectOptions}
                        />
                      </div>
                      <div className="recordings-filter-item">
                        <span className="recordings-filter-label">Status</span>
                        <Select
                          placeholder="All Statuses"
                          allowClear
                          value={params?.status || undefined}
                          onChange={handleStatusFilterChange}
                          options={Object?.keys(RECORDING_STATUS)?.map(
                            (key) => ({
                              value: key,
                              label: RECORDING_STATUS?.[key]?.label,
                            }),
                          )}
                        />
                      </div>
                      <div className="recordings-filter-item">
                        <span className="recordings-filter-label">
                          Recording Date
                        </span>
                        <DatePicker.RangePicker
                          value={
                            params?.recordingDate?.from &&
                            params?.recordingDate?.to
                              ? [
                                  dayjs(params?.recordingDate?.from),
                                  dayjs(params?.recordingDate?.to),
                                ]
                              : undefined
                          }
                          onChange={handleDateRangeChange}
                          disabledDate={disabledDate}
                        />
                      </div>
                    </div>
                  }
                >
                  <Badge
                    count={activeFilterCount}
                    size="small"
                    offset={[-4, 4]}
                  >
                    <Button icon={<FilterOutlined />}>Filters</Button>
                  </Badge>
                </Popover>
              </div>
            </div>
          </>
        }
      >
        <div className="card-body-wrapper">
          {isWorkspaceProjectsEmpty ? (
            <div className="d-flex align-center justify-center h-full">
              <Empty description="There are no projects in this workspace." />
            </div>
          ) : (
            <CommonTable
              size="small"
              columns={columns}
              dataSource={data?.recordings?.data}
              onChange={onChange}
              rowKey={(obj) => obj?.id}
              loadingData={loading}
              bordered
              paginationConfig={{
                current: params?.page,
                total: data?.recordings?.count,
                pageSize: params?.pageSize,
              }}
              onRow={(record) => ({ onClick: () => handleRowClick(record) })}
              rowClassName="recordings-table-row"
            />
          )}
        </div>
      </Card>
    </div>
  );
}

export default Recordings;
